/**
 * useGetDrop — Fetch a Drop by slug.
 *
 * Pure DropsApi consumer: returns the generated `Drop` type (`dropType`,
 * `sections: DropSection[]`, etc.) without any HubResponse adapter. DropPage
 * is the Drop product's canonical consumer; widgets in `web/src/widgets/`
 * accept structurally-compatible inputs at their boundaries.
 *
 * React Query cache key is namespaced separately from useGetHub so a Drop
 * and a Hub with the same slug can never collide in the cache.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { DropsApi, Drop, DropSection, Token } from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";
import { getApiErrorStatus } from "@/utils/getApiErrorStatus";

/* Re-export the generated types so DropPage consumers don't reach into @devour/client directly */
export type { Drop, DropSection };
export type { DropType } from "@devour/client";

async function fetchDrop(slug: string, fullToken?: Token): Promise<Drop | null> {
    try {
        return await new DropsApi(getConfig(fullToken)).getDropsSlugSlug({ slug });
    } catch (err) {
        /*
         * When Drop-In is disabled the backend returns 404 {error:"drop_in_disabled"}.
         * That's a "feature off" state, not a real error — DropInGate redirects the
         * user home. Degrade the 404 to null so it never surfaces an error modal in
         * the brief window before the gate's visibility flag resolves. A non-existent
         * slug also 404s and degrades the same way (DropPage renders its not-found
         * state). Anything other than 404 still throws so real failures show an error.
         */
        /*
         * Since openapi-generator 7.14.0 the client throws a ResponseError that
         * holds the Response on `.response`; before that it threw the raw
         * Response, which carries `.status` directly. getApiErrorStatus reads
         * both, so a cached pre-upgrade bundle still degrades correctly.
         */
        if (getApiErrorStatus(err) === 404) return null;
        throw err;
    }
}

export function useGetDrop(slug?: string) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["drop", slug, fullToken?.id ?? "unauthenticated"],
        queryFn: () => fetchDrop(slug!, fullToken),
        staleTime: 30 * 60000,
        /* Keeps the hero's Hunters count current; the server caches it for the same minute. */
        refetchInterval: 60_000,
        enabled: Boolean(slug),
        placeholderData: keepPreviousData,
    });
}

/** Sections enabled and ordered for render — Drop equivalent of getEnabledSections. */
export function getEnabledDropSections(drop: Drop): DropSection[] {
    return (drop.sections || [])
        .filter(s => s.enabled)
        .sort((a, b) => a.order - b.order);
}
